import { z } from 'zod';
import cpfValidation from '@/utils/cpfValidation';
import { number } from 'yup';

const safeOptionalNumber = z.preprocess((value) => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}, z.number());

export const tipoMatriculaOptions = [
  { value: '5', label: 'Orçamento' },
  { value: '4', label: 'Pré-Matrícula' },
  { value: '1', label: 'Matrícula' },
] as const;

const pessoaSchema = z.object({
  nome: z.string().nullish().or(z.string()
    .min(1, 'O nome é obrigatório').max(50)
    .transform(nome => {
      return nome.trim().split(' ').map(word => {
        return word[0].toLocaleUpperCase().concat(word.substring(1))
      }).join(' ');
    })),
  cpfCnpj: z.string().nullish().or(z.string().refine((cpf: string) => cpfValidation(cpf), 'Digite um CPF válido')),
  orgaoEmissorRg: z.string().nullish(),
  rg: z.string().nullish(),
  ufRg: z.string().nullish(),
  cep: z.string().nullish(),
  uf: z.string().nullish(),
  cidade: z.string().nullish(),
  bairro: z.string().nullish(),
  logradouro: z.string().nullish(),
  numero: z.string().nullish(),
  complemento: z.string().nullish(),
  telefone: z.string().nullish(),
  celular: z.string().nullish(),
  email: z.string().nullish().or(z.string().email('Formato de e-mail inválido').toLowerCase()),
  dataNascimento: z.string().nullish(),
});

const responsavelSchema = z.object({
  pessoaResponsavel: pessoaSchema,
  profissao: z.string().optional(),
  grauParentesco: z.string().optional(),
});

const estudanteSchema = z.object({
  pessoaEstudante: pessoaSchema.optional(),
  alunoEM: z.string().optional().nullable(),
  escolaEM: z.number().optional().nullable(),
  semestresEstudo: z.union([z.string(), z.number()]).optional().nullable(),
  ondeConheceuAcademia: z.number().optional().nullable(),
  ondeConheceuAcademiaOutros: z.string().optional().nullable(),
  observacao: z.string().optional().nullable(),
  turmaId: z.number().optional().nullable(),
  cursoPretendido: z.string().optional().nullable(),
  numeroCabine: z.number().optional().nullable(),
  numeroArmario: z.number().optional().nullable(),
});

export const pagamentoSchema = z.object({
  id: z.number().optional(),
  nome: z.string().optional(),
  tipoPagamento: z.string().optional(),
  ativo: z.boolean().optional(),
  quantidadeParcelas: z.number().optional(),
});

export const pagamentosSchema = z.object({
  id: z.number().optional(),
  pagamentoId: z.number().optional(),
  parcela: z.number().optional(),
  dataParcela: z.string().optional(),
  valor: z.number().optional(),
  cvCartao: z.number().optional(),
  tipoPagamento: z.string().optional(),
  pagamento: pagamentoSchema.optional(),
  nomePagamento: z.string().optional(),
  // Vêm do GET /Aluno e precisam sobreviver ao parse do zod: são o que marca a
  // parcela como trocada por outra forma de pagamento.
  //
  // `.nullable()` não é decoração: na API esses campos são nullable e o JSON
  // traz `null`, não `undefined`. Só com `.optional()` o zod rejeitava a parcela
  // que nunca foi trocada — ou seja, praticamente todas — e a alteração da
  // matrícula não salvava.
  status: z.string().optional().nullable(),
  trocado: z.boolean().optional().nullable(),
  trocaPagamentoNome: z.string().optional().nullable(),
  trocaData: z.string().optional().nullable(),
  // Comprovante escolhido na tela e enviado junto no salvar (opcional).
  comprovante: z
    .object({
      nomeArquivo: z.string(),
      contentType: z.string(),
      conteudoBase64: z.string(),
    })
    .optional()
    .nullable(),
  /** Comprovantes já gravados, só para mostrar na tabela. */
  quantidadeComprovantes: z.number().optional().nullable(),
});

const chequesSchema = z.object({
  id: z.number().optional(),
  banco: z.string().optional(),
  agencia: z.string().optional(),
  conta: z.string().optional(),
  numero: z.number().optional(),
  nomeDonoCheque: z.string().optional(),
});

export const disciplinasSchema = z.object({
  id: z.number().optional(),
  codEspecificaServico: z.string().optional(),
  disciplinaId: z.number().optional(),
  tipo: z.string().optional(),
  nome: z.string().optional(),
  valor: z.number().optional(),
  percDesconto: safeOptionalNumber.optional().default(0),
  valorDesconto: z.number().optional(),
  valorFinal: z.number().optional(),
  descontoManual: z.boolean().optional()
});

export const produtosSchema = z.object({
  id: z.number().optional(),
  produtoId: z.number(),
  nome: z.string().optional(),
  valor: z.number().optional().default(0),
  /** 100% para o produto vinculado a uma específica (cortesia da matrícula). */
  percDesconto: safeOptionalNumber.optional().default(0),
  valorDesconto: z.number().optional().default(0),
  /** Valor efetivamente cobrado — é o que entra no total do pagamento. */
  valorFinal: z.number().optional().default(0),
  /** Específica que trouxe o produto automaticamente; ausente quando adicionado à mão. */
  especificaOrigemId: z.number().nullish(),
});

const unidadeSchema = z.object({
  id: z.number().optional(),
  nome: z.string().optional(),
  fatorAjusteFinanceiro: z.number().optional(),
  ativo: z.boolean().optional(),
  cnpj: z.string().optional(),
  enderecoCompleto: z.string().optional(),
  responsavel: z.string().optional(),
  responsavelCpf: z.string().optional(),
  rg: z.string().optional(),
  orgaoRg: z.string().optional(),
  telefone: z.string().optional(),
  email: z.string().optional(),
});

export const matriculaAlunoSchema = z.object({
  id: z.number().optional(),
  tipoMatricula: z.enum(['1', '4', '5'], {
    required_error: 'Selecione o tipo de matrícula',
  }),
  responsavel: responsavelSchema,
  estudante: estudanteSchema.optional(),
  disciplinas: z.array(disciplinasSchema).optional().default([]),
  produtos: z.array(produtosSchema).optional().default([]),
  pagamentos: z.array(pagamentosSchema).optional().default([]),
  cheques: z.array(chequesSchema).optional().default([]),
  turmaId: z.number().optional(),
  turmaNome: z.string().optional(),
  usuarioId: z.number().optional(),
  usuarioNome: z.string().optional(),
  statusMatriculaId: z.number().optional(),
  unidade: unidadeSchema.optional(),
  // Orçamento não tem número de matrícula: a API devolve null, e `.optional()`
  // sozinho só aceitaria undefined.
  matricula: z.number().nullish(),
  dataInicioAula: z.date().optional(),
  dataFimAula: z.date().optional(),
  formasPagamento: z.string().optional(),
  valorTotalExtenso: z.string().optional(),
  taxaMatricula: z.number().optional(),
  taxaMatriculaExtenso: z.string().optional(),
  pcDescontoAdicional: z.number().optional(),
  valorDescontoAdicional: z.number().optional(),
  valorCabineVip: z.number().optional(),
  valorCabineVipExtenso: z.string().optional(),
  numeroCabineVip: z.number().optional(),
}).superRefine((data, ctx) => {
  const addIssue = (path: (string | number)[], message: string) => {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path,
      message,
    });
  };

  const isBlank = (value: unknown): boolean =>
    value === null || value === undefined || (typeof value === 'string' && value.trim().length === 0);

  type Pessoa = NonNullable<typeof data.responsavel.pessoaResponsavel>;

  const validatePessoaBasica = (
    pessoa: Pessoa | undefined,
    basePath: (string | number)[],
    label: string,
  ) => {
    if (!pessoa) {
      addIssue(basePath, `Dados ${label} são obrigatórios`);
      return;
    }
    if (isBlank(pessoa.nome)) addIssue([...basePath, 'nome'], `Nome ${label} é obrigatório`);
    if (isBlank(pessoa.celular) || (pessoa.celular ?? '').length < 11)
      addIssue([...basePath, 'celular'], `Celular ${label} é obrigatório (11 dígitos)`);
    if (isBlank(pessoa.email)) addIssue([...basePath, 'email'], `E-mail ${label} é obrigatório`);
  };

  const validatePessoaCompleta = (
    pessoa: Pessoa | undefined,
    basePath: (string | number)[],
    label: string,
  ) => {
    validatePessoaBasica(pessoa, basePath, label);
    if (!pessoa) return;
    if (isBlank(pessoa.cep)) addIssue([...basePath, 'cep'], `CEP ${label} é obrigatório`);
    if (isBlank(pessoa.uf)) addIssue([...basePath, 'uf'], `UF ${label} é obrigatória`);
    if (isBlank(pessoa.cidade)) addIssue([...basePath, 'cidade'], `Cidade ${label} é obrigatória`);
    if (isBlank(pessoa.bairro)) addIssue([...basePath, 'bairro'], `Bairro ${label} é obrigatório`);
    if (isBlank(pessoa.logradouro)) addIssue([...basePath, 'logradouro'], `Logradouro ${label} é obrigatório`);
    if (isBlank(pessoa.numero)) addIssue([...basePath, 'numero'], `Número do endereço ${label} é obrigatório`);
    if (isBlank(pessoa.dataNascimento)) addIssue([...basePath, 'dataNascimento'], `Data de nascimento ${label} é obrigatória`);
  };

  const validateEstudanteAcademico = (incluirTurma: boolean) => {
    const estudante = data.estudante;
    if (!estudante) return;
    if (isBlank(estudante.alunoEM)) {
      addIssue(['estudante', 'alunoEM'], 'Informe se o aluno cursa ensino médio');
    } else if (estudante.alunoEM === '1' && !estudante.escolaEM) {
      addIssue(['estudante', 'escolaEM'], 'Escola do ensino médio é obrigatória');
    } else if (estudante.alunoEM === '0') {
      const semestresValue = typeof estudante.semestresEstudo === 'number'
        ? estudante.semestresEstudo
        : (estudante.semestresEstudo ? parseInt(estudante.semestresEstudo as string) : null);
      if (semestresValue === null || semestresValue === undefined || Number.isNaN(semestresValue)) {
        addIssue(['estudante', 'semestresEstudo'], 'Quantidade de semestres de estudo é obrigatória');
      }
    }
    if (!estudante.ondeConheceuAcademia) {
      addIssue(['estudante', 'ondeConheceuAcademia'], 'Informe onde conheceu a academia');
    }
    if (isBlank(estudante.cursoPretendido)) {
      addIssue(['estudante', 'cursoPretendido'], 'Curso pretendido é obrigatório');
    }
  };

  // Validações para Orçamento (5)
  if (data.tipoMatricula === '5') {
    validatePessoaBasica(data.responsavel.pessoaResponsavel, ['responsavel', 'pessoaResponsavel'], 'do responsável');
    if (!data.disciplinas || data.disciplinas.length === 0) {
      addIssue(['disciplinas'], 'Selecione ao menos uma disciplina');
    }
  }

  // Validações para Pré-Matrícula (4)
  if (data.tipoMatricula === '4') {
    validatePessoaCompleta(data.responsavel.pessoaResponsavel, ['responsavel', 'pessoaResponsavel'], 'do responsável');
    if (isBlank(data.responsavel.grauParentesco)) {
      addIssue(['responsavel', 'grauParentesco'], 'Grau de parentesco do responsável é obrigatório');
    }
    if (data.estudante) {
      validatePessoaCompleta(data.estudante.pessoaEstudante, ['estudante', 'pessoaEstudante'], 'do aluno');
      validateEstudanteAcademico(false);
    }
    if (!data.disciplinas || data.disciplinas.length === 0) {
      addIssue(['disciplinas'], 'Selecione ao menos uma disciplina');
    }
  }

  // Validações para Matrícula (1) - mesmas da pré-matrícula + turma + pagamentos
  if (data.tipoMatricula === '1') {
    validatePessoaCompleta(data.responsavel.pessoaResponsavel, ['responsavel', 'pessoaResponsavel'], 'do responsável');
    if (isBlank(data.responsavel.grauParentesco)) {
      addIssue(['responsavel', 'grauParentesco'], 'Grau de parentesco do responsável é obrigatório');
    }
    if (data.estudante) {
      validatePessoaCompleta(data.estudante.pessoaEstudante, ['estudante', 'pessoaEstudante'], 'do aluno');
      validateEstudanteAcademico(true);
    }
    if (!data.disciplinas || data.disciplinas.length === 0) {
      addIssue(['disciplinas'], 'Selecione ao menos uma disciplina');
    }
    if (!data.pagamentos || data.pagamentos.length === 0) {
      addIssue(['pagamentos'], 'Adicione ao menos um pagamento');
    }
  }
});

export type MatriculaAlunoSchema = z.infer<typeof matriculaAlunoSchema>
export type ProdutosSchema = z.infer<typeof produtosSchema>
export type DisciplinasSchema = z.infer<typeof disciplinasSchema>
